import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import PublicChatWidget from "@/components/PublicChatWidget";
import {
  ArrowUpRight, Search, Wrench, TrendingUp, AlertTriangle,
} from "lucide-react";
import {
  T, FONT_DISPLAY, FONT_MONO, PageShell, TopNav, Footer,
  container, eyebrow, h2, italicAccent, btnCoral,
} from "@/lib/hybridTheme";

export default function PublicAIVisibility() {
  const [agent, setAgent] = useState(null);
  useEffect(() => {
    api.get("/public/site-config").then(({ data }) => setAgent(data.agent || null)).catch(() => {});
  }, []);

  return (
    <PageShell testId="public-ai-visibility-page">
      <TopNav activeKey="ai_visibility" />

      {/* HERO */}
      <section style={{ padding: "120px 0 88px", position: "relative", overflow: "hidden" }}>
        {/* mesh glow */}
        <div style={{
          position: "absolute", top: -120, right: -140, width: 700, height: 700,
          background: `radial-gradient(circle, ${T.coral} 0%, transparent 60%)`,
          opacity: 0.14, filter: "blur(60px)", pointerEvents: "none",
        }} />
        <div style={{
          position: "absolute", bottom: -180, left: -140, width: 620, height: 620,
          background: `radial-gradient(circle, ${T.brass} 0%, transparent 60%)`,
          opacity: 0.10, filter: "blur(60px)", pointerEvents: "none",
        }} />
        <div style={container()}>
          <div style={{ maxWidth: 960, position: "relative" }}>
            <div style={{ ...eyebrow(), display: "inline-flex", alignItems: "center", gap: 8 }}>
              <AlertTriangle size={12} /> AI Visibility · Urgent
            </div>
            <h1 style={{
              fontFamily: FONT_DISPLAY, fontSize: "clamp(54px, 7vw, 108px)",
              lineHeight: 0.96, letterSpacing: "-0.035em", fontWeight: 400,
              margin: "18px 0 26px", color: T.ink,
            }}>
              Is Your Business <br />
              <em style={italicAccent(T.coral)}>Invisible</em> to AI?
            </h1>
            <p style={{ fontSize: 22, lineHeight: 1.5, color: T.inkSoft, maxWidth: 760, margin: "0 0 40px" }}>
              ChatGPT, Perplexity, and Google AI now answer your customers&apos; questions
              <strong style={{ color: T.ink }}> before they ever see your website</strong>.
              If AI doesn&apos;t know you exist, neither do they.
            </p>
            <Link
              to="/audit"
              data-testid="hero-snapshot-cta"
              style={{ ...btnCoral(), padding: "18px 30px", fontSize: 16, textDecoration: "none" }}
            >
              Get Your Free AI Visibility Snapshot <ArrowUpRight size={16} />
            </Link>
            <div style={{ marginTop: 22, fontSize: 13, color: T.inkFaint, fontFamily: FONT_MONO, letterSpacing: "0.05em" }}>
              60-second request · No card · PDF delivered to your inbox
            </div>
          </div>
        </div>
      </section>

      {/* PROBLEM */}
      <section style={{ padding: "88px 0", background: T.canvas, borderTop: `1px solid ${T.line}`, borderBottom: `1px solid ${T.line}` }}>
        <div style={container()}>
          <div style={{ maxWidth: 900 }}>
            <div style={eyebrow()}>The shift</div>
            <h2 style={h2()}>
              Search changed.<br />
              Did your <em style={italicAccent(T.coral)}>marketing</em>?
            </h2>
            <p style={{ fontSize: 19, lineHeight: 1.65, color: T.inkSoft, marginTop: 28, maxWidth: 820 }}>
              <strong style={{ color: T.ink }}>37% of consumers</strong> now start their search
              inside AI tools, not Google. Click-through rates on traditional search results have
              dropped as much as <strong style={{ color: T.ink }}>61%</strong> for queries where
              AI now gives the answer directly.
            </p>
            <p style={{ fontSize: 19, lineHeight: 1.65, color: T.inkSoft, marginTop: 20, maxWidth: 820 }}>
              Somewhere right now, a potential customer is asking ChatGPT
              <em style={italicAccent(T.brass)}> &ldquo;who&apos;s the best [your industry] near me&rdquo;</em>
              — and a competitor is getting named instead of you.
            </p>

            <div style={{
              marginTop: 44, display: "grid", gap: 20,
              gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
            }}>
              <StatCard n="37%" label="of consumers start their search in AI tools" />
              <StatCard n="61%" label="drop in CTR when AI answers the query directly" accent={T.brass} />
              <StatCard n="24/7" label="AI recommends businesses — yours or your competitor's" accent={T.sage} />
            </div>
          </div>
        </div>
      </section>

      {/* WHAT WE DO */}
      <section style={{ padding: "96px 0" }}>
        <div style={container()}>
          <div style={{ maxWidth: 780, marginBottom: 44 }}>
            <div style={eyebrow()}>What we do</div>
            <h2 style={h2()}>Three plays to <em style={italicAccent(T.coral)}>get named</em> by AI.</h2>
          </div>
          <div style={{ display: "grid", gap: 20, gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))" }}>
            <OfferCard
              icon={Search}
              tag="Diagnostic"
              title="AI Visibility Audit"
              body="We test 20-30 real customer questions across ChatGPT, Perplexity, and Google AI to show exactly where you stand — and who's beating you."
              accent={T.coral}
            />
            <OfferCard
              icon={Wrench}
              tag="Fix"
              title="Visibility Sprint"
              body="We fix what's broken: missing content, structure AI can't read, and gaps your competitors have already closed."
              accent={T.brass}
              featured
            />
            <OfferCard
              icon={TrendingUp}
              tag="Ongoing"
              title="Monthly Retainer"
              body="Ongoing content and monitoring so you stay visible as AI search keeps changing — not a one-time fix."
              accent={T.sage}
            />
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section style={{ padding: "96px 0", background: T.canvas, borderTop: `1px solid ${T.line}`, borderBottom: `1px solid ${T.line}` }}>
        <div style={container()}>
          <div style={{ maxWidth: 780, marginBottom: 48 }}>
            <div style={eyebrow()}>How it works</div>
            <h2 style={h2()}>Three steps, <em style={italicAccent(T.brass)}>zero fluff</em>.</h2>
          </div>
          <div style={{ display: "grid", gap: 24, gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))" }}>
            <Step
              n="01"
              title="Test"
              body="We run your business against real customer questions across every major AI engine — ChatGPT, Perplexity, Gemini, Google AI."
            />
            <Step
              n="02"
              title="Report"
              body="You get a clear report: where you're invisible, why, and which competitors are winning the answer."
            />
            <Step
              n="03"
              title="Fix & maintain"
              body="We fix it — content, structure, and ongoing visibility — so you stay named as AI keeps evolving."
            />
          </div>
        </div>
      </section>

      {/* SOCIAL PROOF placeholder */}
      <section style={{ padding: "96px 0" }}>
        <div style={container()}>
          <div style={{ maxWidth: 780, marginBottom: 32 }}>
            <div style={eyebrow()}>Results</div>
            <h2 style={h2()}>Client wins <em style={italicAccent(T.coral)}>coming soon.</em></h2>
          </div>
          <div
            style={{
              border: `1px dashed ${T.line}`, padding: "56px 40px", textAlign: "center",
              background: `${T.panel}`, position: "relative", overflow: "hidden",
            }}
            data-testid="social-proof-placeholder"
          >
            <div style={{ fontFamily: FONT_MONO, fontSize: 11, letterSpacing: "0.16em", textTransform: "uppercase", color: T.brass, fontWeight: 700, marginBottom: 12 }}>
              Placeholder — case studies live here
            </div>
            <p style={{ fontSize: 18, color: T.inkSoft, maxWidth: 640, margin: "0 auto", lineHeight: 1.6 }}>
              We&apos;re running audits for our first cohort right now.
              Real numbers, real competitor call-outs and screenshots will land here as they come in.
            </p>
            <div style={{ marginTop: 22, fontSize: 13, color: T.inkFaint, fontFamily: FONT_MONO }}>
              Want to be in the first cohort? Grab a free snapshot below.
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section style={{ padding: "72px 0 128px" }}>
        <div style={container()}>
          <div style={{
            background: T.ink, color: T.cream, padding: "72px 56px",
            position: "relative", overflow: "hidden",
            border: `1px solid ${T.coral}`,
          }}>
            <div style={{ position: "absolute", top: -100, right: -100, width: 380, height: 380, background: `radial-gradient(circle, ${T.coral} 0%, transparent 65%)`, opacity: 0.5, borderRadius: "50%" }} />
            <div style={{ position: "absolute", bottom: -140, left: -100, width: 340, height: 340, background: `radial-gradient(circle, ${T.brass} 0%, transparent 65%)`, opacity: 0.35, borderRadius: "50%" }} />
            <div style={{ position: "relative", display: "grid", gridTemplateColumns: "1.5fr 1fr", gap: 44, alignItems: "center" }}>
              <div>
                <div style={{ ...eyebrow(), color: T.brass }}>Free · No obligation</div>
                <h3 style={{ fontFamily: FONT_DISPLAY, fontSize: "clamp(40px, 4.5vw, 60px)", fontWeight: 400, letterSpacing: "-0.03em", lineHeight: 1, margin: "16px 0 20px", color: T.cream }}>
                  Find out where you stand — <em style={italicAccent(T.coral)}>free.</em>
                </h3>
                <p style={{ fontSize: 18, lineHeight: 1.6, opacity: 0.85, maxWidth: 520, margin: 0 }}>
                  Get a snapshot of how your business currently appears in AI search.
                  No obligation, takes 2 minutes to request.
                </p>
              </div>
              <div style={{ textAlign: "right" }}>
                <Link
                  to="/audit"
                  data-testid="final-snapshot-cta"
                  style={{ ...btnCoral(), padding: "20px 32px", fontSize: 16, textDecoration: "none" }}
                >
                  Get My Free Snapshot <ArrowUpRight size={16} />
                </Link>
                <div style={{ marginTop: 14, fontSize: 12, color: "rgba(247,244,238,0.55)", fontFamily: FONT_MONO, letterSpacing: "0.05em" }}>
                  Snapshot arrives via email · Full audit optional
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <PublicChatWidget agent={agent} />
    </PageShell>
  );
}

function StatCard({ n, label, accent = T.coral }) {
  return (
    <div style={{
      background: T.panel, border: `1px solid ${T.line}`, padding: "28px 26px",
      position: "relative", overflow: "hidden",
    }}>
      <div style={{ position: "absolute", top: 0, left: 0, width: 3, height: "100%", background: accent }} />
      <div style={{
        fontFamily: FONT_DISPLAY, fontSize: 56, fontWeight: 400, lineHeight: 1,
        letterSpacing: "-0.03em", color: T.ink,
      }}>{n}</div>
      <div style={{ fontSize: 14, color: T.inkSoft, marginTop: 12, lineHeight: 1.5 }}>{label}</div>
    </div>
  );
}

function OfferCard({ icon: Icon, tag, title, body, accent, featured }) {
  return (
    <div style={{
      background: T.panel,
      border: `1px solid ${featured ? accent : T.line}`,
      padding: "36px 32px",
      position: "relative",
      transform: featured ? "translateY(-6px)" : "none",
      boxShadow: featured ? `0 30px 60px -20px rgba(10,14,26,0.7), inset 0 0 0 1px ${accent}22` : "none",
    }}>
      {featured && (
        <div style={{
          position: "absolute", top: -1, right: -1, background: accent, color: "#0A0E1A",
          padding: "6px 14px", fontSize: 11, fontFamily: FONT_MONO, letterSpacing: "0.1em",
          textTransform: "uppercase", fontWeight: 700,
        }}>
          Most popular
        </div>
      )}
      <div style={{
        width: 44, height: 44,
        background: `${accent}22`, border: `1px solid ${accent}55`,
        color: accent, display: "inline-flex", alignItems: "center", justifyContent: "center",
        marginBottom: 22,
      }}>
        <Icon size={20} strokeWidth={2} />
      </div>
      <div style={{ fontSize: 11, fontFamily: FONT_MONO, letterSpacing: "0.12em", textTransform: "uppercase", color: accent, fontWeight: 700 }}>
        {tag}
      </div>
      <div style={{ fontFamily: FONT_DISPLAY, fontSize: 32, fontStyle: "italic", fontWeight: 400, color: T.ink, marginTop: 8, letterSpacing: "-0.02em" }}>
        {title}
      </div>
      <p style={{ fontSize: 15, lineHeight: 1.6, color: T.inkSoft, marginTop: 16 }}>{body}</p>
    </div>
  );
}

function Step({ n, title, body }) {
  return (
    <div style={{ background: T.panel, border: `1px solid ${T.line}`, padding: "32px 30px" }}>
      <div style={{
        fontFamily: FONT_DISPLAY, fontSize: 68, fontWeight: 400, letterSpacing: "-0.04em",
        lineHeight: 0.9, color: T.coral, opacity: 0.85,
      }}>
        {n}
      </div>
      <div style={{ fontFamily: FONT_DISPLAY, fontSize: 26, fontStyle: "italic", fontWeight: 400, color: T.ink, marginTop: 8 }}>
        {title}
      </div>
      <p style={{ fontSize: 15, lineHeight: 1.6, color: T.inkSoft, marginTop: 14 }}>{body}</p>
    </div>
  );
}
